import React from 'react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { IconPicker } from '@/components/ui/icon-picker'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { sdk } from '@/lib/sdk'
import { useTranslation } from 'react-i18next'
import { type Space, type SpaceModule, type FeedWidgets, DEFAULT_FEED_WIDGETS } from '@/types/spaces'
import { useAuth } from '@/lib/AuthContext'
import { type ModuleConfig } from '@/types/space-wizard'
import { CoursesModuleFields } from './wizard/modules/CoursesModuleFields'
import { FeedModuleFields, type FeedScope } from './wizard/modules/FeedModuleFields'
import { Call4PapersModuleFields } from './wizard/modules/Call4PapersModuleFields'
import { CertificatesModuleFields } from './wizard/modules/CertificatesModuleFields'
import { SingleItemFollowUp } from './SingleItemFollowUp'
import { useRoute } from '@/lib/RouteContext'
import { getModule, hasPosting, isSingleItemConfig, isSingleSpaceEmpty, moduleIcon, moduleIconName, MODULE_NAMESPACES } from '@/modules/registry'
import { useCommunity } from '@/lib/CommunityContext'
import { generateSlug, slugErrorFrom, typedSlug } from '@/lib/slug'
import { VisibilityPicker } from '@/components/ui/visibility-picker'
import { WhoCanPostSelector, type WhoCanPostValue } from './wizard/shared/WhoCanPostSelector'
import { AccessGrantPicker } from '@/components/ui/access-grant-picker'
import { LockedDisplayPicker } from '@/components/ui/locked-display-picker'
import { SpaceLayoutField } from './SpaceLayoutField'
import { loadModuleCatalog, moduleLayoutsOf } from './moduleCatalog'
import { offeredLayouts } from '@/lib/spaceLayout'
import type { ModuleLayouts, SpaceLayout } from '@/types/spaceLayout'
import {
  emptyAccessGrants,
  grantCount,
  toAccessGrantInput,
  type AccessGrants,
  type RequiredBySection,
  type SpaceLockedDisplay,
  type Visibility
} from '@/types/access'

type C4PConfig = NonNullable<ModuleConfig['call4papers']>
/** What `GET /call4papers/spaces/:id` answers, as far as this dialog reads it. */
type Call4PapersSpaceRead = {
  mode?: C4PConfig['spaceMode']
  whoCanOpenCall?: C4PConfig['whoCanOpenCall']
  call?: { id?: string; type?: C4PConfig['callType'] } | null
} | null
type ApiFailure = { response?: { data?: { message?: string } } } | null

interface EditSpaceDialogProps {
  space: Space
  open: boolean
  onOpenChange: (open: boolean) => void
  onSpaceUpdated?: () => void
  /** `module`: the module's "Configurar" opens straight on the module's settings. */
  focus?: 'module'
}

export const EditSpaceDialog = ({ space, open, onOpenChange, onSpaceUpdated, focus }: EditSpaceDialogProps) => {
  const moduleSectionRef = React.useRef<HTMLDivElement>(null)
  const { t } = useTranslation(['common', 'navigation', ...MODULE_NAMESPACES])
  const { community } = useCommunity()
  const { navigate } = useRoute()
  /* Saved as a one-item space with no item yet: the dialog continues to the item's form. */
  const [followUp, setFollowUp] = React.useState<{ module: SpaceModule; path: string | null } | null>(null)
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [error, setError] = React.useState('')
  const [slugError, setSlugError] = React.useState<string | null>(null)

  const [title, setTitle] = React.useState('')
  const [description, setDescription] = React.useState('')
  const [slug, setSlug] = React.useState('')
  const [icon, setIcon] = React.useState('')
  const [module, setModule] = React.useState<Space['module']>('content')
  /**
   * Keyed by module, like the wizard's, so switching module in the dialog does
   * not throw away what was typed for the previous one.
   *
   * What the API stores and returns is the **inner** object, so it is unwrapped
   * on open and wrapped again on save.
   */
  const [moduleConfig, setModuleConfig] = React.useState<ModuleConfig>({})
  /* F-162: a top-level column of the space, not part of `moduleConfig`. */
  const [feedScope, setFeedScope] = React.useState<FeedScope | null>(null)
  /* F-181: the Feed's side column. Owner and admin only (the API answers 403 to anyone else). */
  const { hasRole } = useAuth()
  const canEditFeedWidgets = hasRole(['owner', 'admin'])
  const [feedWidgets, setFeedWidgets] = React.useState<FeedWidgets>(DEFAULT_FEED_WIDGETS)
  /* F-198 "Exibição": owner and admin only (crit. 5); the options come from the module catalog,
     which only they can read. Without options the field is not drawn and `layout` is not sent. */
  const [layout, setLayout] = React.useState<SpaceLayout | null>(null)
  const [layoutOptions, setLayoutOptions] = React.useState<ModuleLayouts | null>(null)
  const [visibility, setVisibility] = React.useState<Visibility>('members')
  /* Who can create inside the space (APP-50). Sent only when the space told
     us its current value, so an unknown one is never overwritten. */
  const [canCreate, setCanCreate] = React.useState<WhoCanPostValue | null>(null)
  const [access, setAccess] = React.useState<AccessGrants>(emptyAccessGrants())
  const [requiredBySection, setRequiredBySection] = React.useState<RequiredBySection | null>(null)
  const [fromProducts, setFromProducts] = React.useState<AccessGrants['products']>([])
  const [lockedDisplay, setLockedDisplay] = React.useState<SpaceLockedDisplay>('inherit')
  const [externalUrl, setExternalUrl] = React.useState('')
  /**
   * The one call of a single-mode call4papers space.
   *
   * "Simple or advanced" is a property of the **call**, not of the space — the
   * space's config only seeds it at creation. So the control reads this call's
   * type and writes back to it; without the id it would be a switch wired to
   * nothing.
   */
  const [singleCall, setSingleCall] = React.useState<{ id: string; type: string } | null>(null)
  /** Who issues, as the certificates endpoint holds it — compared on save so an unchanged rule is not rewritten. */
  const [savedWhoCanIssue, setSavedWhoCanIssue] = React.useState<'staff' | 'admins' | null>(null)
  /* The module's own setup (`ModuleSetup`): `setupLoaded` is false until its
     terms are on screen (they come from the module's own endpoint, and only
     for whoever may manage them), and `setupErrors` are its field errors. */
  const [setupLoadedFor, setSetupLoadedFor] = React.useState<string | null>(null)
  const [setupErrors, setSetupErrors] = React.useState<unknown>(null)
  const setup = getModule(module)?.setup
  const setupLoaded = setupLoadedFor === module

  React.useEffect(() => {
    if (open && space) {
      setModule(space.module)
      setFeedScope(space.feedScope ?? null)
      setFeedWidgets({ ...DEFAULT_FEED_WIDGETS, ...(space.feedWidgets || {}) })
      setTitle(space.title)
      setDescription(space.description || '')
      setSlug(space.slug)
      setIcon(space.icon || moduleIconName(space.module))
      setSetupErrors(null)
      const spaceSetup = getModule(space.module)?.setup
      setModuleConfig(
        spaceSetup?.persist.type === 'moduleConfig'
          /* Stored in the space itself (attendance, F-78): the setup reads its own slice. */
          ? ({ [space.module]: spaceSetup.persist.fromSpace(space) } as ModuleConfig)
          : space.moduleConfig ? ({ [space.module]: space.moduleConfig } as ModuleConfig) : {}
      )
      setSetupLoadedFor(spaceSetup?.persist.type === 'moduleConfig' ? space.module : null)

      /* call4papers is the exception: its settings live in `Call4PapersSpace`
         (`mode`, `whoCanOpenCall`), not in `Space.moduleConfig`. Reading the
         column here would show "several calls" for a space that is single, and
         saving it would write a field nothing reads — a decoy control. */
      setSingleCall(null)
      if (space.module === 'call4papers') {
        sdk.call4papers
          .getSpace(space.id)
          .then((read: unknown) => {
            const config = read as Call4PapersSpaceRead
            const call = config?.call ?? null
            setSingleCall(call?.id ? { id: call.id, type: call.type ?? 'simple' } : null)
            setModuleConfig((prev) => ({
              ...prev,
              call4papers: {
                ...(prev.call4papers || {}),
                spaceMode: config?.mode || 'multi',
                whoCanOpenCall: config?.whoCanOpenCall || 'staff',
                callType: call?.type ?? prev.call4papers?.callType
              }
            }))
          })
          .catch(() => {
            /* Leave the field on its default rather than blocking the dialog —
               everything else here is editable without it. */
          })
      }
      /* Certificates keep "who issues" in `CertificateSpace`, behind its own
         endpoint — the same split as call4papers (F-42 §4.3). */
      setSavedWhoCanIssue(null)
      if (space.module === 'certificates') {
        sdk.certificates
          .getSpace(space.id)
          .then((config) => {
            setSavedWhoCanIssue(config.whoCanIssue)
            setModuleConfig((prev) => ({ ...prev, certificates: { whoCanIssue: config.whoCanIssue } }))
          })
          .catch(() => {
            /* The rule stays as it is on the server; the field shows the default. */
          })
      }
      /* Setups with their own endpoint (classifieds F-72, downloads F-75): load the saved terms. */
      if (spaceSetup?.persist.type === 'endpoint') {
        const moduleKey = space.module
        spaceSetup.persist
          .load(space.id)
          .then((config) => {
            if (!config) return
            setModuleConfig((prev) => ({ ...prev, [moduleKey]: config }))
            setSetupLoadedFor(moduleKey)
          })
          .catch(() => {
            /* Without the terms there is nothing to edit; the rest of the dialog still saves. */
          })
      }
      setVisibility(space.visibility)
      setCanCreate((space.canCreate as WhoCanPostValue | undefined) ?? null)
      setLayout(space.layout ?? null)
      setLayoutOptions(null)
      if (canEditFeedWidgets && community?.id) {
        const moduleKey = space.module
        loadModuleCatalog(community.id, () => sdk.spaces.listModules())
          .then((catalog) => setLayoutOptions(moduleLayoutsOf(catalog, moduleKey)))
          .catch(() => { /* no options: the field stays hidden */ })
      }

      /* The listing hands over a trimmed space; the access blocks come with the
         single read. Fetching them here means the modal never draws a stale
         "required by the section" that somebody changed in another tab. */
      setAccess(space.access ?? emptyAccessGrants())
      setRequiredBySection(space.requiredBySection ?? null)
      setFromProducts(space.accessFromProducts ?? [])
      setLockedDisplay(space.lockedDisplay ?? 'inherit')

      sdk.spaces
        .get(space.id)
        .then((fresh: unknown) => {
          const data = ((fresh as { data?: unknown } | null)?.data ?? fresh) as Partial<Space> | null
          if (!data) return
          setAccess(data.access ?? emptyAccessGrants())
          setRequiredBySection(data.requiredBySection ?? null)
          setFromProducts(data.accessFromProducts ?? [])
          if (data.lockedDisplay) setLockedDisplay(data.lockedDisplay)
          if (data.canCreate) setCanCreate(data.canCreate)
          if ('layout' in data) setLayout(data.layout ?? null)
        })
        .catch(() => {
          /* Leave what the listing gave. The server validates the save anyway,
             so a failed refresh costs an accurate preview, not correctness. */
        })

      setExternalUrl(space.externalUrl || '')
      setError('')
      setSlugError(null)
      setIsSubmitting(false)
      setFollowUp(null)
    }
  }, [open, space])

  /* "Configurar": once the module's settings are on screen, bring them into view. */
  React.useEffect(() => {
    if (!open || focus !== 'module') return
    const id = window.setTimeout(() => moduleSectionRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' }), 150)
    return () => window.clearTimeout(id)
  }, [open, focus, setupLoaded])

  const layoutChoices = canEditFeedWidgets ? offeredLayouts(module, layoutOptions?.options) : []

  /* A setup's confirmation (`confirmSave`, F-203a retention period) is open; saving waits for it. */
  const [confirming, setConfirming] = React.useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!title.trim()) {
      setError(t('common:validation.titleRequired'))
      return
    }

    if (module === 'url' && !externalUrl.trim()) {
      setError(t('common:spaces.externalUrlRequired'))
      return
    }

    if (setup && setupLoaded) {
      const invalid = setup.validate((moduleConfig as Record<string, unknown>)[module] ?? setup.defaultConfig(t), t)
      setSetupErrors(invalid)
      if (invalid) return
      if (setup.confirmSave?.needed((moduleConfig as Record<string, unknown>)[module], space)) {
        setConfirming(true)
        return
      }
    }

    await save()
  }

  const save = async () => {
    setIsSubmitting(true)

    try {
      const saved = await sdk.spaces.update(space.id, {
        title: title.trim(),
        description: description.trim() || undefined,
        slug: generateSlug(slug) || undefined,
        icon: icon.trim() || undefined,
        module,
        visibility,
        ...(canCreate ? { canCreate } : {}),
        externalUrl: externalUrl.trim() || undefined,
        /* Only when it is private: sending an empty set alongside another
           visibility would erase grants nobody asked to erase. */
        access: visibility === 'private' ? toAccessGrantInput(access) : undefined,
        lockedDisplay,
        /* F-198: only when the field is drawn and the choice changed. */
        ...(layoutChoices.length > 1 && layout && layout !== (space.layout ?? null) ? { layout } : {}),
        /* Only when it changed: the column lives apart from `moduleConfig`, so nothing else is touched. */
        ...(module === 'feed' && feedScope && feedScope !== space.feedScope ? { feedScope } : {}),
        /* Only the widgets that changed: the server merges, so the others stay as stored. */
        ...(() => {
          if (module !== 'feed' || !canEditFeedWidgets) return {}
          const stored = { ...DEFAULT_FEED_WIDGETS, ...(space.feedWidgets || {}) }
          const changed = Object.fromEntries(Object.entries(feedWidgets).filter(([key, on]) => stored[key as keyof FeedWidgets] !== on))
          return Object.keys(changed).length ? { feedWidgets: changed } : {}
        })(),
        /* Only this module's slice goes up, matching what create sends. An
           empty object would clear a config the dialog never rendered.
           call4papers is excluded: its own endpoint below is the source of
           truth, and writing both would give the space two of them. */
        moduleConfig: setup?.persist.type === 'endpoint' || module === 'certificates'
          /* Certificates keep "who issues" behind their own endpoint too (below); so do the setups with one. */
          ? undefined
          : module === 'call4papers'
            ? undefined
            : setup?.persist.type === 'moduleConfig'
              /* What this screen owns replaces its keys; what it does not (attendance retention and kiosk) goes back untouched. */
              ? setup.persist.toModuleConfig((moduleConfig as Record<string, unknown>)[module] ?? setup.defaultConfig(t), space.moduleConfig ?? null)
            : module === 'courses'
              /* The mode is the only key; the rest of what is stored goes back untouched. The API refuses
                 "one course" with two or more courses (409), and nothing changes then. */
              ? { ...(moduleConfig.courses || {}), mode: moduleConfig.courses?.mode === 'single' ? 'single' : 'multi' }
            : module === 'url'
              /* The address is edited in its own field above; keep the stored
                 copy in step instead of sending back the one from creation. */
              ? { ...(moduleConfig.url || {}), externalUrl: externalUrl.trim() }
              /* The rest round-trip what is stored, untouched: phase 1 of F-64
                 took their controls out, not their data. */
              : ((moduleConfig as Record<string, unknown>)[module] ?? undefined)
      })

      const whoCanIssue = moduleConfig.certificates?.whoCanIssue
      if (module === 'certificates' && whoCanIssue && whoCanIssue !== savedWhoCanIssue) {
        await sdk.certificates.updateSpace(space.id, whoCanIssue)
      }

      if (setup?.persist.type === 'endpoint' && setupLoaded && (moduleConfig as Record<string, unknown>)[module]) {
        try {
          await setup.persist.save(space.id, (moduleConfig as Record<string, unknown>)[module], t)
        } catch (err: unknown) {
          /* The space itself is saved; the terms were refused (a category with ads, say). Stay open and say where. */
          setSetupErrors(setup.persist.errorsFromSaveFailure?.(err) ?? null)
          setError((err as ApiFailure)?.response?.data?.message || t('common:errors.updateFailed'))
          return
        }
      }

      if (module === 'call4papers' && moduleConfig.call4papers?.spaceMode) {
        /* Its own route, which also refuses `single` on a space that already has
           more than one live call — a rule the space form cannot enforce. */
        await sdk.call4papers.updateSpace(space.id, {
          mode: moduleConfig.call4papers.spaceMode,
          /* Only in a hub: a single-call space shows no such field, and the
             stored rule stays as it was. */
          ...(moduleConfig.call4papers.spaceMode === 'multi'
            ? { whoCanOpenCall: moduleConfig.call4papers.whoCanOpenCall || 'staff' }
            : {})
        })
      }

      /* The type goes to the call, which is where it lives. Only when it
         actually changed: a PUT that repeats the current type would still run
         the server's preset and clear an advanced call's review settings. */
      const chosenType = moduleConfig.call4papers?.callType
      if (
        module === 'call4papers' &&
        moduleConfig.call4papers?.spaceMode === 'single' &&
        singleCall &&
        chosenType &&
        chosenType !== singleCall.type
      ) {
        await sdk.call4papers.update(singleCall.id, { type: chosenType })
      }

      /* One item and none yet: continue to the item instead of leaving an empty single space. */
      const singleModule = singleModeOf()
      if (singleModule && (await isSingleSpaceEmpty(singleModule, space.id))) {
        setFollowUp({ module: singleModule, path: await spacePathOf(saved) })
        return
      }

      onOpenChange(false)
      onSpaceUpdated?.()
    } catch (err: unknown) {
      const message = (err as ApiFailure)?.response?.data?.message
      const onSetup = setup?.errorsFromApi?.(err)
      if (onSetup) {
        setSetupErrors(onSetup)
        setError(message || t('common:errors.updateFailed'))
        return
      }
      const onSlug = slugErrorFrom(err)
      if (onSlug) setSlugError(onSlug)
      else setError(message || t('common:errors.updateFailed'))
    } finally {
      setIsSubmitting(false)
    }
  }

  /** The module, when this save leaves the space as one item (call, course, survey, file). */
  const singleModeOf = (): SpaceModule | null => {
    /* Only modules that can tell whether the space is still empty offer the follow-up. */
    if (!getModule(module)?.singleItem?.isEmpty) return null
    /* A setup saved by its own endpoint is only known once it loaded (downloads). */
    if (setup?.persist.type === 'endpoint' && !setupLoaded) return null
    return isSingleItemConfig(module, (moduleConfig as Record<string, unknown>)[module]) ? module : null
  }

  /** Where the space lives now (the slug may just have changed); `null` stays where the person is. */
  const spacePathOf = async (saved: unknown): Promise<string | null> => {
    const read = (value: unknown) => {
      const data = ((value as { data?: unknown } | null)?.data ?? value) as { slug?: string; sectionSlug?: string | null; section?: { slug?: string } | null } | null
      return data?.slug && (data.sectionSlug || data.section?.slug) ? `/${data.sectionSlug || data.section?.slug}/${data.slug}` : null
    }
    const fromSave = read(saved)
    if (fromSave) return fromSave
    try { return read(await sdk.spaces.get(space.id)) } catch { return null }
  }

  /* Leaving the item step, either way: the space is saved, so the menu learns about it now. */
  const finishFollowUp = (created: boolean) => {
    const path = followUp?.path
    setFollowUp(null)
    onOpenChange(false)
    onSpaceUpdated?.()
    if (created && path) navigate(path)
  }

  /**
   * The module's own settings, the same fields the creation wizard shows.
   *
   * Only call4papers has any here. Certificates put "who issues" in the "who
   * can post" slot, the external link's address has its own field above, and
   * the controls the other modules had were taken out in F-64 phase 1 because
   * nothing read them. The switch mirrors `CreateSpaceDialog` (F-03).
   */
  const renderModuleFields = () => {
    if (module === 'feed') {
      return (
        <FeedModuleFields
          value={feedScope ?? 'section'}
          onChange={setFeedScope}
          disabled={isSubmitting}
          {...(canEditFeedWidgets ? { widgets: feedWidgets, onWidgetsChange: setFeedWidgets } : {})}
        />
      )
    }
    if (module === 'courses') {
      return (
        <CoursesModuleFields
          config={moduleConfig.courses || {}}
          onChange={(config) => setModuleConfig({ ...moduleConfig, courses: config })}
          variant="settings"
          disabled={isSubmitting}
        />
      )
    }
    if (setup) {
      if (!setupLoaded) return null
      return (
        <setup.Fields
          config={(moduleConfig as Record<string, unknown>)[module] ?? setup.defaultConfig(t)}
          onChange={(config) => { setModuleConfig({ ...moduleConfig, [module]: config }); setSetupErrors(null) }}
          errors={setupErrors}
          disabled={isSubmitting}
          context="edit"
          spaceId={space.id}
          space={space}
        />
      )
    }
    if (module !== 'call4papers') return null
    return (
      <Call4PapersModuleFields
        config={moduleConfig.call4papers || {}}
        onChange={(config) => setModuleConfig({ ...moduleConfig, call4papers: config })}
        variant="settings"
        disabled={isSubmitting}
      />
    )
  }

  /* `icon` is a kebab-case name that came from the database, so it can be empty
     or name something lucide does not have. `(icon || '')` keeps the `.split`
     from throwing before the `|| Home` fallback ever gets a chance. */
  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next && followUp) finishFollowUp(false); else onOpenChange(next) }}>
      {/* Same width as `CreateSpaceDialog`. Editing shows the very same module
          settings as creating, and at 540px those field groups wrapped into a
          different shape than the one the person had just filled in. The
          primitive already scrolls, so no `overflow-y-auto` here. */}
      <DialogContent guard className="flex max-h-[85dvh] max-w-5xl flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>{t('common:spaces.editSpace')}</DialogTitle>
          <DialogDescription>
            {followUp ? t('common:spaces.followUp.description') : t('common:spaces.editDescription')}
          </DialogDescription>
        </DialogHeader>

        {followUp ? (
          <SingleItemFollowUp
            module={followUp.module}
            spaceId={space.id}
            callType={moduleConfig.call4papers?.callType === 'advanced' ? 'advanced' : 'simple'}
            onDone={() => finishFollowUp(true)}
            onLater={() => finishFollowUp(false)}
          />
        ) : (
        /* Column flex so the footer is pinned and only the body scrolls. With
           the module settings in, the form outgrew the dialog and Cancel/Save
           went under the fold — the same defect as APP-17 in the content
           dialog. */
        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col space-y-4">
          {error && (
            <div className="p-3 text-sm text-red-600 bg-red-50 rounded-lg">
              {error}
            </div>
          )}

          {/* Two columns: what the space *is* on the left, who may open it and
              how the module behaves on the right. One column made a dialog
              twice as tall as the screen, so the fields somebody came to change
              were usually below the fold. */}
          {/* Not an even split: the right column holds the four visibility
              buttons, and at half the dialog "Privada" fell to a second row on
              its own. The left column has nothing that needs the width. */}
          <div className="grid min-h-0 flex-1 grid-cols-1 gap-x-8 gap-y-5 overflow-y-auto pr-1 md:grid-cols-[minmax(0,0.82fr)_minmax(0,1.18fr)]">
            <div className="space-y-4">
            {/* The module is fixed once the space exists (T9): switching it would orphan what the space holds. */}
            <div className="space-y-2" data-testid="edit-space-module">
              <span className="text-sm font-medium">{t('common:spaces.module')}</span>
              <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-white text-primary-600">
                  {React.createElement(moduleIcon(module), { className: 'w-4 h-4' })}
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-gray-900">{t(`common:modules.${module}`)}</span>
                  <span className="block text-xs text-gray-500">{t(`common:modules.descriptions.${module}`)}</span>
                </span>
              </div>
              <p className="text-xs text-gray-500">{t('common:spaces.moduleFixedHint')}</p>
            </div>

            <div className="grid grid-cols-[60px_1fr] gap-2">
              <div className="space-y-2">
                <label htmlFor="icon" className="text-sm font-medium">
                  {t('common:fields.icon')}
                </label>
                <IconPicker value={icon} onChange={setIcon} disabled={isSubmitting} id="icon" />
              </div>
              <div className="space-y-2">
                <label htmlFor="title" className="text-sm font-medium">
                  {t('common:fields.title')} *
                </label>
                <Input
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={t('common:spaces.titlePlaceholder')}
                  disabled={isSubmitting}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="slug" className="text-sm font-medium">
                {t('common:fields.slug')}
              </label>
              <Input
                id="slug"
                value={slug}
                onChange={(e) => { setSlug(typedSlug(e.target.value)); setSlugError(null) }}
                placeholder={t('common:spaces.slugPlaceholder')}
                disabled={isSubmitting}
                aria-invalid={!!slugError}
                className={slugError ? 'border-red-500 focus-visible:ring-red-500/20' : undefined}
              />
              {slugError ? (
                <p className="text-xs text-red-600">{slugError}</p>
              ) : (
                <p className="text-xs text-gray-500">
                  {t('common:spaces.slugHint')}
                </p>
              )}
            </div>

            {module === 'url' && (
              <div className="space-y-2">
                <label htmlFor="externalUrl" className="text-sm font-medium">
                  {t('common:spaces.externalUrl')} *
                </label>
                <Input
                  id="externalUrl"
                  type="url"
                  value={externalUrl}
                  onChange={(e) => setExternalUrl(e.target.value)}
                  placeholder={t('common:spaces.externalUrlPlaceholder')}
                  disabled={isSubmitting}
                  required={module === 'url'}
                />
                <p className="text-xs text-gray-500">
                  {t('common:spaces.externalUrlHint')}
                </p>
              </div>
            )}

            <div className="space-y-2">
              <label htmlFor="description" className="text-sm font-medium">
                {t('common:fields.description')}
              </label>
              <Textarea aiFieldKind="other"
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('common:spaces.descriptionPlaceholder')}
                disabled={isSubmitting}
                /* Five rows, not three: the right column is the taller of the
                   two, and the description is the one field here that can take
                   the slack without pushing anything down. */
                rows={5}
              />
            </div>

            {layoutOptions && (
              <SpaceLayoutField
                options={layoutChoices}
                value={layout}
                defaultLayout={layoutOptions.default}
                onChange={setLayout}
                disabled={isSubmitting}
              />
            )}

            </div>

            <div className="space-y-4 md:border-l md:border-gray-100 md:pl-8">
            <VisibilityPicker
              value={visibility}
              onChange={setVisibility}
              disabled={isSubmitting}
              sectionVisibility={requiredBySection?.visibility}
              sectionTitle={requiredBySection?.sectionTitle}
            />

            {module === 'certificates' ? (
              <CertificatesModuleFields
                config={moduleConfig.certificates || {}}
                onChange={(config) => setModuleConfig({ ...moduleConfig, certificates: config })}
                disabled={isSubmitting}
              />
            ) : !hasPosting(module) ? null : (
              <WhoCanPostSelector
                value={canCreate ?? 'anyone'}
                onChange={setCanCreate}
                disabled={isSubmitting}
              />
            )}

            {visibility === 'private' && (
              <div className="rounded-lg border p-3">
                <AccessGrantPicker
                  value={access}
                  onChange={setAccess}
                  requiredBySection={requiredBySection}
                  fromProducts={fromProducts}
                  communityId={community?.id}
                  disabled={isSubmitting}
                />
              </div>
            )}

            {/* F-161a: nothing to choose for a public space; disabled with the reason when only the team gets in. */}
            {visibility !== 'public' && (
              <LockedDisplayPicker
                kind="space"
                value={lockedDisplay}
                onChange={setLockedDisplay}
                disabled={isSubmitting}
                adminOnly={
                  (requiredBySection?.visibility === 'private' && grantCount(requiredBySection) === 0)
                  || (visibility === 'private' && grantCount(access) + fromProducts.length === 0 && requiredBySection?.visibility !== 'private')
                }
              />
            )}

            {(module === 'call4papers' || module === 'courses' || module === 'feed' || (setup && setupLoaded)) && (
              <div ref={moduleSectionRef} className={cn('space-y-4 border-t pt-4', focus === 'module' && 'scroll-mt-2')} data-testid="edit-space-module-config">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-900">
                  {t('common:spaces.moduleConfiguration')}
                </h3>
                {renderModuleFields()}
              </div>
            )}
            </div>
          </div>

          <DialogFooter className="flex-shrink-0 border-t pt-4">
            <Button data-dialog-cancel
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              {t('common:actions.cancel')}
            </Button>
            <Button
              type="submit"
              variant="secondary"
              disabled={isSubmitting || !title.trim()}
            >
              {isSubmitting ? t('common:spaces.updatingSpace') : t('common:actions.save')}
            </Button>
          </DialogFooter>
        </form>
        )}
        {setup?.confirmSave && (
          <setup.confirmSave.Dialog
            spaceId={space.id}
            config={(moduleConfig as Record<string, unknown>)[module]}
            open={confirming}
            onCancel={() => setConfirming(false)}
            onConfirm={() => { setConfirming(false); void save() }}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}
